import type { Bootstrap, Plan, PlanCreate, PlanUpdate, PrayerTimes, Preferences, PreferencesUpdate, Weather } from '@obv/api-client';
import { ApiError, uuid4 } from '@obv/api-client';
import { addDays, comparePlans, mergeChanges } from '@obv/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryClient } from './client';
import { keys } from './keys';

export const PLAN_WINDOW_BACK = 30;
export const PLAN_WINDOW_AHEAD = 90;

export interface PlanWindow {
  items: Plan[];
  /** Server-side watermark for the delta feed (never derived from the device clock). */
  since: string;
}

export function useBootstrap(enabled = true) {
  return useQuery({
    queryKey: keys.bootstrap(),
    queryFn: () => api.bootstrap(),
    enabled,
    refetchInterval: 15 * 60_000,
  });
}

export function useWeather(enabled = true) {
  return useQuery<Weather, ApiError>({
    queryKey: keys.weather(),
    queryFn: () => api.weather(),
    enabled,
    refetchInterval: 5 * 60_000,
    retry: (count, err) => (err.code === 'weather.pending' ? count < 30 : err.status === 0 || err.status >= 500 ? count < 3 : false),
    retryDelay: (_n, err) => (err.code === 'weather.pending' ? 10_000 : 5_000),
  });
}

export function planWindow(today: string) {
  return { from: addDays(today, -PLAN_WINDOW_BACK), to: addDays(today, PLAN_WINDOW_AHEAD) };
}

async function fetchWindow(from: string, to: string, serverTime: string): Promise<PlanWindow> {
  const items: Plan[] = [];
  let cursor: string | undefined;
  do {
    const page = await api.plans.list({ from, to, cursor, limit: 200 });
    items.push(...page.items);
    cursor = page.next_cursor || undefined;
  } while (cursor);
  let since = serverTime;
  for (const p of items) if (p.updated_at > since) since = p.updated_at;
  return { items: items.sort(comparePlans), since };
}

/** All plans in the navigable window around `today`, kept fresh by the delta feed. */
export function usePlanWindow(today: string, serverTime: string | undefined) {
  const { from, to } = planWindow(today);
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: keys.plans.window(from, to),
    queryFn: () => fetchWindow(from, to, serverTime!),
    enabled: !!serverTime,
    staleTime: 10 * 60_000,
  });
  useQuery({
    queryKey: [...keys.plans.window(from, to), 'changes'],
    enabled: query.isSuccess,
    refetchInterval: 60_000,
    queryFn: async () => {
      const cur = qc.getQueryData<PlanWindow>(keys.plans.window(from, to));
      if (!cur) return 0;
      const res = await api.plans.changes(cur.since);
      if (res.items.length) {
        qc.setQueryData<PlanWindow>(keys.plans.window(from, to), (old) =>
          old ? { items: mergeChanges(old.items, res.items).filter((p) => p.date >= from && p.date <= to), since: res.next_since } : old,
        );
      } else if (res.next_since !== cur.since) {
        qc.setQueryData<PlanWindow>(keys.plans.window(from, to), (old) => (old ? { ...old, since: res.next_since } : old));
      }
      return res.items.length;
    },
  });
  return query;
}

// ---- Plan mutations (optimistic, offline-replayable) ----

type CreateVars = { body: PlanCreate; key: string; tempId: string };
type UpdateVars = { id: string; body: PlanUpdate; key: string };
type DeleteVars = { id: string; key: string };

function patchAllWindows(fn: (items: Plan[]) => Plan[]) {
  queryClient.setQueriesData<PlanWindow>({ queryKey: keys.plans.all() }, (old) =>
    old && Array.isArray(old.items) ? { ...old, items: fn(old.items) } : old,
  );
}

function snapshot() {
  return queryClient.getQueriesData<PlanWindow>({ queryKey: keys.plans.all() });
}

function restore(snap: ReturnType<typeof snapshot> | undefined) {
  if (snap) for (const [k, v] of snap) queryClient.setQueryData(k, v);
}

// Defaults let paused mutations restored from storage resume after an app relaunch.
queryClient.setMutationDefaults(['plans', 'create'], {
  mutationFn: (v: CreateVars) => api.plans.create(v.body, v.key),
});
queryClient.setMutationDefaults(['plans', 'update'], {
  mutationFn: (v: UpdateVars) => api.plans.update(v.id, v.body, v.key),
});
queryClient.setMutationDefaults(['plans', 'delete'], {
  mutationFn: (v: DeleteVars) => api.plans.remove(v.id, v.key),
});

export function useCreatePlan() {
  return useMutation<Plan, ApiError, CreateVars, { snap: ReturnType<typeof snapshot> }>({
    mutationKey: ['plans', 'create'],
    onMutate: async (v) => {
      await queryClient.cancelQueries({ queryKey: keys.plans.all() });
      const snap = snapshot();
      const temp: Plan = {
        id: v.tempId,
        title: v.body.title,
        date: v.body.date,
        time: v.body.all_day ? null : (v.body.time ?? null),
        all_day: !!v.body.all_day,
        starts_at: null,
        member_id: v.body.member_id ?? null,
        completed: false,
        completed_at: null,
        source: 'obv',
        read_only: false,
        updated_at: new Date(0).toISOString(),
        deleted_at: null,
      };
      patchAllWindows((items) => [...items, temp].sort(comparePlans));
      return { snap };
    },
    onSuccess: (plan, v) => patchAllWindows((items) => items.map((p) => (p.id === v.tempId ? plan : p)).sort(comparePlans)),
    onError: (_e, _v, ctx) => restore(ctx && ctx.snap),
  });
}

export function useUpdatePlan() {
  return useMutation<Plan, ApiError, UpdateVars, { snap: ReturnType<typeof snapshot> }>({
    mutationKey: ['plans', 'update'],
    onMutate: async (v) => {
      await queryClient.cancelQueries({ queryKey: keys.plans.all() });
      const snap = snapshot();
      patchAllWindows((items) =>
        items
          .map((p) => {
            if (p.id !== v.id) return p;
            const next = { ...p, ...v.body } as Plan;
            if (v.body.all_day) next.time = null;
            return next;
          })
          .sort(comparePlans),
      );
      return { snap };
    },
    onSuccess: (plan) => patchAllWindows((items) => items.map((p) => (p.id === plan.id ? plan : p)).sort(comparePlans)),
    onError: (_e, _v, ctx) => restore(ctx && ctx.snap),
  });
}

export function useDeletePlan() {
  return useMutation<void, ApiError, DeleteVars, { snap: ReturnType<typeof snapshot> }>({
    mutationKey: ['plans', 'delete'],
    onMutate: async (v) => {
      await queryClient.cancelQueries({ queryKey: keys.plans.all() });
      const snap = snapshot();
      patchAllWindows((items) => items.filter((p) => p.id !== v.id));
      return { snap };
    },
    onError: (e, _v, ctx) => {
      if (e.code !== 'plan.not_found') restore(ctx && ctx.snap);
    },
  });
}

export const newMutationKey = uuid4;

// ---- Preferences ----

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation<Preferences, ApiError, PreferencesUpdate, { prev?: Bootstrap }>({
    mutationFn: (body) => api.preferences.update(body),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: keys.bootstrap() });
      const prev = qc.getQueryData<Bootstrap>(keys.bootstrap());
      if (prev && prev.preferences) {
        qc.setQueryData<Bootstrap>(keys.bootstrap(), {
          ...prev,
          preferences: { ...prev.preferences, ...body, updated_at: new Date().toISOString() } as Preferences,
        });
      }
      return { prev };
    },
    onSuccess: (prefs) => {
      const cur = qc.getQueryData<Bootstrap>(keys.bootstrap());
      if (cur) qc.setQueryData<Bootstrap>(keys.bootstrap(), { ...cur, preferences: prefs });
    },
    onError: (_e, _v, ctx) => {
      if (ctx && ctx.prev) qc.setQueryData(keys.bootstrap(), ctx.prev);
    },
  });
}

export function useResetPreferences() {
  const qc = useQueryClient();
  return useMutation<Preferences, ApiError, void>({
    mutationFn: () => api.preferences.reset(),
    onSuccess: (prefs) => {
      const cur = qc.getQueryData<Bootstrap>(keys.bootstrap());
      if (cur) qc.setQueryData<Bootstrap>(keys.bootstrap(), { ...cur, preferences: prefs });
    },
  });
}

// ---- Salat (prayer) times ----

/** Keyed by the household-local date so a new day fetches the new day's times. */
export function usePrayerTimes(today: string, enabled = true) {
  return useQuery<PrayerTimes, ApiError>({
    queryKey: keys.prayer.times(today),
    queryFn: () => api.prayer.times(),
    enabled,
    staleTime: 30 * 60_000,
    refetchInterval: 60 * 60_000,
    retry: (count, err) => (err.code === 'prayer.pending' ? count < 30 : err.status === 0 || err.status >= 500 ? count < 3 : false),
    retryDelay: (_n, err) => (err.code === 'prayer.pending' ? 10_000 : 5_000),
  });
}

export function usePrayerMethods(enabled = true) {
  return useQuery({ queryKey: keys.prayer.methods(), queryFn: () => api.prayer.methods().then((r) => r.items), enabled, staleTime: Infinity });
}
